import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { campaignQuestionCount, type CampaignLaunchAnswers } from 'fym-shared/src/music24/campaignLaunch';
import type { YouTubePostSettings } from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../../../../common/types';
import { ConfirmDialog } from '../../../../../../common/views/modals/confirmDialog/ConfirmDialog';
import type { PendingLaunch } from './launchModel';
import { launchMessages } from './launchMessages';
import { LaunchTikTokQuestion, type TikTokLaunchAnswer } from './LaunchTikTokQuestion';
import { LaunchYouTubeQuestion } from './LaunchYouTubeQuestion';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from '../Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_TIKTOK_ANSWER: TikTokLaunchAnswer = { settings: null, waits: true };

interface PostsLaunchQuestionsProps {
  accountId: number;
  /** The launch that waits for answers. Null: the dialog is closed. */
  pending: PendingLaunch | null;
  /** The sentence under the title. The default speaks of a campaign. */
  description?: string;
  isBusy: boolean;
  /** The sentence of a launch that failed while the dialog was open. */
  error?: string;
  onAnswer: (answers: CampaignLaunchAnswers) => void;
  onCancel: () => void;
}

type QuestionsProps = Omit<PostsLaunchQuestionsProps, 'pending'> & { pending: PendingLaunch };

/** The open dialog: its own component, so each launch starts with empty answers. */
const QuestionsComponent: React.FC<QuestionsProps> = ({
  accountId,
  pending,
  description,
  isBusy,
  error,
  onAnswer,
  onCancel,
}) => {
  const intl = useIntl();
  const youtubeQuestion = pending.questions.find((question) => question.platform === 'youtube');
  const tiktokQuestion = pending.questions.find((question) => question.platform === 'tiktok');
  const [youtube, setYouTube] = useState<YouTubePostSettings | null>(null);
  const [tiktok, setTikTok] = useState<TikTokLaunchAnswer>(NO_TIKTOK_ANSWER);
  const tiktokSkipped = tiktokQuestion !== undefined && tiktok.settings === null && !tiktok.waits;
  const postCount = pending.postCount - (tiktokSkipped ? (tiktokQuestion?.postCount ?? 0) : 0);
  const waits = (youtubeQuestion !== undefined && youtube === null) || (tiktokQuestion !== undefined && tiktok.waits);

  const confirm = useCallback(() => {
    const answers: CampaignLaunchAnswers = {};
    if (youtubeQuestion !== undefined && youtube !== null) {
      answers.youtube = youtube;
    }
    if (tiktokQuestion !== undefined && tiktok.settings !== null) {
      answers.tiktok = tiktok.settings;
    }
    onAnswer(answers);
  }, [onAnswer, tiktok.settings, tiktokQuestion, youtube, youtubeQuestion]);

  return (
    <ConfirmDialog
      isOpen
      tone="neutral"
      testId="launch-questions"
      title={intl.formatMessage(launchMessages.questionsTitle, { count: campaignQuestionCount(pending.questions) })}
      description={description ?? intl.formatMessage(launchMessages.questionsBody)}
      confirmLabel={intl.formatMessage(launchMessages.questionsConfirm, { count: postCount })}
      cancelLabel={intl.formatMessage(launchMessages.questionsCancel)}
      isBusy={isBusy}
      isConfirmDisabled={waits || postCount === 0}
      error={error}
      onConfirm={confirm}
      onCancel={onCancel}
    >
      <div className={styles.launchQuestions}>
        {youtubeQuestion === undefined ? null : (
          <LaunchYouTubeQuestion accountId={accountId} question={youtubeQuestion} onAnswer={setYouTube} />
        )}
        {tiktokQuestion === undefined ? null : (
          <LaunchTikTokQuestion accountId={accountId} question={tiktokQuestion} onAnswer={setTikTok} />
        )}
      </div>
    </ConfirmDialog>
  );
};

const Questions = TypedMemo(QuestionsComponent);

/**
 * "Before we schedule: 2 quick answers" (owner report 2026-10-10). One row for each platform whose
 * posts need the user: YouTube (who can see the videos, made for kids) and TikTok (who can see the
 * posts). The answers go on every draft of the platform, then the posts are scheduled. The plan
 * and the Calendar show this same dialog (owner decision 2026-10-11). It asks for each launch:
 * "Made for kids" and the TikTok answers are never stored.
 */
const PostsLaunchQuestionsComponent: React.FC<PostsLaunchQuestionsProps> = ({
  accountId,
  pending,
  description,
  isBusy,
  error,
  onAnswer,
  onCancel,
}) => {
  if (pending === null) {
    return null;
  }
  return (
    <Questions
      key={pending.postUuids.join(',')}
      accountId={accountId}
      pending={pending}
      description={description}
      isBusy={isBusy}
      error={error}
      onAnswer={onAnswer}
      onCancel={onCancel}
    />
  );
};

export const PostsLaunchQuestions = TypedMemo(PostsLaunchQuestionsComponent);
